import { useEffect, useMemo, useState } from "react";
import { Calculator, Edit3, Eye, Package, Plus, Search, Trash2 } from "lucide-react";
import http from "../api/http";
import Modal from "../components/Modal";
import { consumirIntencionNuevo } from "../utils/navigationIntent";

const tiposProducto = ["Materia prima", "Intermedio", "Final"];
const tiposMedida = ["Peso", "Volumen"];
const unidadesPorTipo = {
  Peso: ["mg", "g", "kg"],
  Volumen: ["ml", "l"],
};

const estadoInicial = () => ({
  nombre: "",
  proveedoresIds: [],
  stockMinimo: "",
  tipo: "Materia prima",
  tipoMedida: "Peso",
  unidadMedida: "kg",
  recetaRendimiento: {
    tipoMedida: "Peso",
    cantidad: "",
    unidadMedida: "kg",
  },
  margenGananciaPorcentaje: "",
  costoCalculadoPorUnidad: 0,
  precioVentaPorUnidad: 0,
  presentacionesFraccionamiento: [],
  receta: [],
  materialesProduccion: [],
  activo: true,
});

const ingredienteVacio = () => ({
  productoId: "",
  producto: "",
  tipo: "",
  tipoMedida: "Peso",
  cantidad: "",
  unidadMedida: "kg",
});

const materialVacio = () => ({
  materialId: "",
  nombre: "",
  cantidad: "",
});

const permiteFormula = (tipo) => ["Intermedio", "Final"].includes(tipo);
const unidadesDe = (tipoMedida) => unidadesPorTipo[tipoMedida] || unidadesPorTipo.Peso;
const getOptionValue = (option) => (typeof option === "string" ? option : option.value);
const getOptionLabel = (option) => (typeof option === "string" ? option : option.label);
const normalizarTexto = (value) => String(value || "").trim().toLowerCase();
const idReferencia = (value) => String(typeof value === "object" ? value?._id || "" : value || "");
const unidadesCosto = {
  mg: { tipo: "Peso", base: "kg", factor: 0.000001 },
  g: { tipo: "Peso", base: "kg", factor: 0.001 },
  kg: { tipo: "Peso", base: "kg", factor: 1 },
  ml: { tipo: "Volumen", base: "l", factor: 0.001 },
  l: { tipo: "Volumen", base: "l", factor: 1 },
};
const convertirUnidad = (cantidad, origen, destino) => {
  const unidadOrigen = unidadesCosto[normalizarTexto(origen)];
  const unidadDestino = unidadesCosto[normalizarTexto(destino)];
  if (!unidadOrigen || !unidadDestino || unidadOrigen.tipo !== unidadDestino.tipo) return null;
  return (Number(cantidad || 0) * unidadOrigen.factor) / unidadDestino.factor;
};
const formatearImporte = (value) => Number(value || 0).toLocaleString("es-AR", {
  style: "currency",
  currency: "ARS",
});
const formatearCantidad = (value) => Number(value || 0).toLocaleString("es-AR", {
  maximumFractionDigits: 6,
});

export default function Productos() {
  const [productos, setProductos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [materialesProduccion, setMaterialesProduccion] = useState([]);
  const [formulario, setFormulario] = useState(estadoInicial);
  const [editando, setEditando] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [productoSimulacion, setProductoSimulacion] = useState(null);
  const [presentacionesGenerales, setPresentacionesGenerales] = useState([]);
  const [modalPresentacionesGenerales, setModalPresentacionesGenerales] = useState(false);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");
  const [busquedaProveedor, setBusquedaProveedor] = useState("");
  const [proveedoresAbiertos, setProveedoresAbiertos] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [error, setError] = useState("");

  const cargarProductos = async () => {
    try {
      setCargando(true);
      setError("");
      const { data } = await http.get("/productos");
      setProductos(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.error || "No se pudieron cargar los productos.");
    } finally {
      setCargando(false);
    }
  };

  const cargarMaterialesProduccion = async () => {
    try {
      const { data } = await http.get("/materiales-produccion");
      setMaterialesProduccion(Array.isArray(data) ? data : []);
    } catch {
      setMaterialesProduccion([]);
    }
  };

  const cargarProveedores = async () => {
    try {
      const { data } = await http.get("/proveedores");
      setProveedores(Array.isArray(data) ? data : []);
    } catch {
      setProveedores([]);
    }
  };

  const cargarPresentacionesGenerales = async () => {
    try {
      const { data } = await http.get("/productos/presentaciones-generales");
      setPresentacionesGenerales(Array.isArray(data) ? data : []);
    } catch {
      setPresentacionesGenerales([]);
    }
  };

  useEffect(() => {
    cargarProductos();
    cargarMaterialesProduccion();
    cargarProveedores();
    cargarPresentacionesGenerales();
    if (consumirIntencionNuevo()) setModalAbierto(true);
  }, []);

  const productosFiltrados = useMemo(() => {
    const query = busqueda.toLowerCase().trim();
    return productos.filter((producto) => {
      const coincideTipo = !filtroTipo ||
        (filtroTipo === "Costo pendiente"
          ? producto.costoPendienteActualizacion === true
          : producto.tipo === filtroTipo);
      const coincideBusqueda = !query ||
        `${producto.codigoSku || ""} ${producto.nombre || ""} ${producto.tipo || ""}`
          .toLowerCase()
          .includes(query);
      return coincideTipo && coincideBusqueda;
    });
  }, [productos, busqueda, filtroTipo]);

  const ingredientesDisponibles = useMemo(
    () =>
      productos.filter(
        (producto) =>
          producto.activo !== false &&
          ["Materia prima", "Intermedio"].includes(producto.tipo) &&
          (!editando || producto._id !== editando._id),
      ),
    [productos, editando],
  );

  const proveedoresFiltrados = useMemo(() => {
    const query = busquedaProveedor.trim().toLowerCase();
    if (!query) return proveedores;
    return proveedores.filter((proveedor) =>
      `${proveedor.codigoProveedor || ""} ${proveedor.razonSocial || ""} ${proveedor.numeroDocumento || ""}`
        .toLowerCase()
        .includes(query),
    );
  }, [busquedaProveedor, proveedores]);

  const proveedoresAsignadosA = (productoId) =>
    proveedores
      .filter((proveedor) =>
        (proveedor.productos || []).some(
          (item) => String(item.productoCatalogoId || "") === String(productoId || ""),
        ),
      )
      .map((proveedor) => proveedor._id);

  const abrirCrear = () => {
    setEditando(null);
    setFormulario(estadoInicial());
    setBusquedaProveedor("");
    setProveedoresAbiertos(false);
    setMensaje("");
    setError("");
    setModalAbierto(true);
  };

  const abrirEditar = (producto) => {
    const tipoMedida = producto.tipoMedida || "Peso";
    const rendimiento = producto.recetaRendimiento || {};
    setEditando(producto);
    setFormulario({
      ...estadoInicial(),
      ...producto,
      proveedoresIds: proveedoresAsignadosA(producto._id),
      tipo: producto.tipo || "Materia prima",
      tipoMedida,
      unidadMedida: producto.unidadMedida || unidadesDe(tipoMedida)[0],
      recetaRendimiento: {
        tipoMedida: rendimiento.tipoMedida || tipoMedida,
        cantidad: rendimiento.cantidad || "",
        unidadMedida: rendimiento.unidadMedida || producto.unidadMedida || unidadesDe(tipoMedida)[0],
      },
      margenGananciaPorcentaje: producto.margenGananciaPorcentaje ?? "",
      receta: Array.isArray(producto.receta)
        ? producto.receta.map((item) => ({
            ...ingredienteVacio(),
            ...item,
            cantidad: item.cantidad ?? item.kilosPorPaquete25kg ?? "",
          }))
        : [],
      materialesProduccion: Array.isArray(producto.materialesProduccion)
        ? producto.materialesProduccion.map((item) => ({
            ...materialVacio(),
            ...item,
          }))
        : [],
      activo: producto.activo ?? true,
    });
    setMensaje("");
    setError("");
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setEditando(null);
    setBusquedaProveedor("");
    setProveedoresAbiertos(false);
    setFormulario(estadoInicial());
  };

  const alternarProveedor = (proveedorId) => {
    setFormulario((prev) => {
      const seleccionados = new Set((prev.proveedoresIds || []).map(String));
      if (seleccionados.has(String(proveedorId))) seleccionados.delete(String(proveedorId));
      else seleccionados.add(String(proveedorId));
      return { ...prev, proveedoresIds: [...seleccionados] };
    });
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormulario((prev) => {
      if (name === "tipo") {
        return {
          ...prev,
          tipo: value,
          receta: permiteFormula(value) ? prev.receta : [],
          materialesProduccion: permiteFormula(value) ? prev.materialesProduccion : [],
          recetaRendimiento: permiteFormula(value)
            ? prev.recetaRendimiento
            : { ...prev.recetaRendimiento, cantidad: "" },
          margenGananciaPorcentaje: permiteFormula(value) ? prev.margenGananciaPorcentaje : "",
        };
      }

      if (name === "tipoMedida") {
        const unidadMedida = unidadesDe(value)[0];
        return {
          ...prev,
          tipoMedida: value,
          unidadMedida,
          recetaRendimiento: {
            ...prev.recetaRendimiento,
            tipoMedida: value,
            unidadMedida,
          },
        };
      }

      if (name === "unidadMedida") {
        return {
          ...prev,
          unidadMedida: value,
          recetaRendimiento: {
            ...prev.recetaRendimiento,
            unidadMedida: value,
          },
        };
      }

      return {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };
    });
  };

  const handleRendimientoChange = (event) => {
    const { name, value } = event.target;
    setFormulario((prev) => ({
      ...prev,
      recetaRendimiento: {
        ...prev.recetaRendimiento,
        tipoMedida: prev.tipoMedida,
        unidadMedida: prev.unidadMedida,
        [name]: value,
      },
    }));
  };

  const agregarIngrediente = () => {
    setFormulario((prev) => ({ ...prev, receta: [...(prev.receta || []), ingredienteVacio()] }));
  };

  const actualizarIngrediente = (index, name, value) => {
    setFormulario((prev) => ({
      ...prev,
      receta: (prev.receta || []).map((item, itemIndex) => {
        if (itemIndex !== index) return item;

        if (name === "productoId") {
          const producto = ingredientesDisponibles.find((candidate) => candidate._id === value);
          if (!producto) return { ...ingredienteVacio(), _id: item._id };

          const tipoMedida = producto.tipoMedida || "Peso";
          return {
            ...item,
            productoId: value,
            producto: producto.nombre,
            tipo: producto.tipo,
            tipoMedida,
            unidadMedida: producto.unidadMedida || unidadesDe(tipoMedida)[0],
          };
        }

        if (name === "tipoMedida") {
          return { ...item, tipoMedida: value, unidadMedida: unidadesDe(value)[0] };
        }

        return { ...item, [name]: value };
      }),
    }));
  };

  const quitarIngrediente = (index) => {
    setFormulario((prev) => ({
      ...prev,
      receta: (prev.receta || []).filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const agregarMaterial = () => {
    setFormulario((prev) => ({
      ...prev,
      materialesProduccion: [...(prev.materialesProduccion || []), materialVacio()],
    }));
  };

  const actualizarMaterial = (index, name, value) => {
    setFormulario((prev) => ({
      ...prev,
      materialesProduccion: (prev.materialesProduccion || []).map((item, itemIndex) => {
        if (itemIndex !== index) return item;
        if (name !== "materialId") return { ...item, [name]: value };
        const material = materialesProduccion.find((candidate) => String(candidate._id) === String(value));
        return material
          ? { ...item, materialId: value, nombre: material.nombre }
          : { ...materialVacio(), _id: item._id };
      }),
    }));
  };

  const quitarMaterial = (index) => {
    setFormulario((prev) => ({
      ...prev,
      materialesProduccion: (prev.materialesProduccion || []).filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const validar = () => {
    if (!formulario.nombre.trim()) {
      setError("El nombre es obligatorio.");
      return false;
    }

    if (permiteFormula(formulario.tipo)) {
      if (Number(formulario.recetaRendimiento.cantidad || 0) <= 0) {
        setError("Indica la cantidad producida por la formula.");
        return false;
      }

      const incompleto = (formulario.receta || []).some(
        (item) => !item.producto || Number(item.cantidad || 0) <= 0,
      );

      if (incompleto) {
        setError("Completa producto, cantidad y medida de cada ingrediente.");
        return false;
      }

      const materialIncompleto = (formulario.materialesProduccion || []).some(
        (item) => !item.materialId || Number(item.cantidad || 0) <= 0,
      );
      if (materialIncompleto) {
        setError("Completa material y cantidad de cada material de produccion.");
        return false;
      }
    }

    return true;
  };

  const guardarProducto = async (event) => {
    event.preventDefault();
    if (!validar()) return;

    try {
      setCargando(true);
      setError("");
      const formularioSinPrecio = { ...formulario };
      delete formularioSinPrecio.precioPorUnidad;
      delete formularioSinPrecio.precioPorKg;
      const payload = {
        ...formularioSinPrecio,
        proveedoresIds: formulario.proveedoresIds || [],
        stockMinimo: Math.max(Number(formulario.stockMinimo || 0), 0),
        margenGananciaPorcentaje: permiteFormula(formulario.tipo)
          ? Math.max(Number(formulario.margenGananciaPorcentaje || 0), 0)
          : 0,
        recetaRendimiento: {
          ...formulario.recetaRendimiento,
          tipoMedida: formulario.tipoMedida,
          cantidad: Number(formulario.recetaRendimiento.cantidad || 0),
          unidadMedida: formulario.unidadMedida,
        },
        receta: permiteFormula(formulario.tipo)
          ? (formulario.receta || []).map((item) => ({
              productoId: item.productoId || null,
              producto: item.producto,
              tipo: item.tipo,
              tipoMedida: item.tipoMedida,
              cantidad: Number(item.cantidad || 0),
              unidadMedida: item.unidadMedida,
            }))
          : [],
        materialesProduccion: permiteFormula(formulario.tipo)
          ? (formulario.materialesProduccion || []).map((item) => ({
              materialId: item.materialId,
              nombre: item.nombre,
              cantidad: Number(item.cantidad || 0),
            }))
          : [],
      };

      if (editando) {
        await http.put(`/productos/${editando._id}`, payload);
        setMensaje("Producto actualizado.");
      } else {
        await http.post("/productos", payload);
        setMensaje("Producto creado.");
      }
      window.dispatchEvent(new Event("notificaciones:actualizar"));
      cerrarModal();
      await cargarProductos();
    } catch (err) {
      setError(err.response?.data?.error || "No se pudo guardar el producto.");
    } finally {
      setCargando(false);
    }
  };

  const eliminarProducto = async (producto) => {
    if (!window.confirm(`Eliminar ${producto.nombre}?`)) return;
    try {
      setCargando(true);
      setError("");
      await http.delete(`/productos/${producto._id}`);
      window.dispatchEvent(new Event("notificaciones:actualizar"));
      setMensaje("Producto eliminado.");
      await cargarProductos();
    } catch (err) {
      setError(err.response?.data?.error || "No se pudo eliminar el producto.");
    } finally {
      setCargando(false);
    }
  };

  const guardarPrecioSimulado = async ({ margenGananciaPorcentaje, costoCalculadoPorUnidad, precioVentaPorUnidad, presentacionesFraccionamiento }) => {
    try {
      setCargando(true);
      setError("");
      await http.put(`/productos/${productoSimulacion._id}`, {
        ...productoSimulacion,
        margenGananciaPorcentaje,
        costoCalculadoPorUnidad,
        precioVentaPorUnidad,
        precioVentaActualizadoEn: new Date().toISOString(),
        costoPendienteActualizacion: false,
        presentacionesFraccionamiento,
      });
      setProductoSimulacion(null);
      setMensaje("Costo y precio de venta actualizados.");
      await cargarProductos();
    } catch (err) {
      setError(err.response?.data?.error || "No se pudo guardar el precio de venta.");
    } finally {
      setCargando(false);
    }
  };

  const guardarPresentacionesGenerales = async (presentaciones) => {
    try {
      setCargando(true);
      setError("");
      const { data } = await http.put("/productos/presentaciones-generales", { presentaciones });
      setPresentacionesGenerales(Array.isArray(data) ? data : []);
      setModalPresentacionesGenerales(false);
      setMensaje("Presentaciones generales actualizadas.");
      await cargarProductos();
    } catch (err) {
      setError(err.response?.data?.error || "No se pudieron guardar las presentaciones.");
    } finally {
      setCargando(false);
    }
  };

  return (
    <div>
      <div className="page-header actions-only">
        <div className="header-actions">
          <button className="btn btn-primary" onClick={abrirCrear}>
            <Plus size={18} />
            Nuevo producto
          </button>
          <button className="btn" type="button" onClick={() => setModalPresentacionesGenerales(true)}>
            <Package size={18} />
            Presentaciones generales
          </button>
        </div>
      </div>

      {mensaje && <div className="alert alert-ok">{mensaje}</div>}
      {error && !modalAbierto && <div className="alert alert-error">{error}</div>}

      <section className="panel">
        <div className="toolbar">
          <div>
            <h2>Productos cargados</h2>
            <p>Total: {productosFiltrados.length}</p>
          </div>
          <div className="search">
            <Search size={17} />
            <input
              placeholder="Buscar por SKU, nombre o tipo..."
              value={busqueda}
              onChange={(event) => setBusqueda(event.target.value)}
            />
          </div>
          <div className="field catalog-type-filter">
            <label>Tipo de producto</label>
            <select value={filtroTipo} onChange={(event) => setFiltroTipo(event.target.value)}>
              <option value="">Todos</option>
              <option value="Materia prima">Materia prima</option>
              <option value="Intermedio">Intermedio</option>
              <option value="Final">Final</option>
              <option value="Costo pendiente">Costo pendiente</option>
            </select>
          </div>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>SKU</th>
                <th>Nombre</th>
                <th>Tipo</th>
                <th>Medida</th>
                <th>Stock minimo</th>
                <th>Formula</th>
                <th>Precio venta</th>
                <th>Activo</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {cargando ? (
                <tr><td colSpan="9">Cargando...</td></tr>
              ) : productosFiltrados.length === 0 ? (
                <tr><td colSpan="9">Sin productos.</td></tr>
              ) : (
                productosFiltrados.map((producto) => (
                  <tr key={producto._id}>
                    <td><strong>{producto.codigoSku}</strong></td>
                    <td>{producto.nombre}</td>
                    <td><span className="badge warn">{producto.tipo}</span></td>
                    <td>{producto.tipoMedida || "-"} ({producto.unidadMedida || "-"})</td>
                    <td>{Number(producto.stockMinimo || 0).toLocaleString("es-AR")} {producto.unidadMedida}</td>
                    <td>
                      {permiteFormula(producto.tipo)
                        ? `${producto.receta?.length || 0} ingredientes / ${producto.materialesProduccion?.length || 0} materiales`
                        : "-"}
                    </td>
                    <td>
                      {permiteFormula(producto.tipo) && Number(producto.precioVentaPorUnidad || 0) > 0
                        ? `${formatearImporte(producto.precioVentaPorUnidad)} / ${producto.tipoMedida === "Volumen" ? "L" : "kg"}`
                        : "-"}
                    </td>
                    <td><span className={`badge ${producto.activo ? "ok" : "off"}`}>{producto.activo ? "Si" : "No"}</span></td>
                    <td>
                      <div className="row-actions">
                        <button className="btn action-icon-button" type="button" title="Detalle" aria-label="Detalle" onClick={() => setDetalle(producto)}>
                          <Eye size={16} />
                        </button>
                        <button className="btn action-icon-button" type="button" title="Editar" aria-label="Editar" onClick={() => abrirEditar(producto)}>
                          <Edit3 size={16} />
                        </button>
                        {permiteFormula(producto.tipo) && (
                          <button
                            className={`btn action-icon-button cost-calculator-button ${producto.costoPendienteActualizacion ? "has-pending-cost" : ""}`}
                            type="button"
                            title={producto.costoPendienteActualizacion ? "Costos actualizados: revisar calculo" : "Simular costos"}
                            aria-label={producto.costoPendienteActualizacion ? "Costos actualizados: revisar calculo" : "Simular costos"}
                            onClick={() => setProductoSimulacion(producto)}
                          >
                            <Calculator size={16} />
                            {producto.costoPendienteActualizacion && <span className="pending-cost-dot" aria-hidden="true" />}
                          </button>
                        )}
                        <button className="btn btn-danger action-icon-button" type="button" title="Eliminar" aria-label="Eliminar" onClick={() => eliminarProducto(producto)}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <Modal
        abierto={modalAbierto}
        titulo={editando ? "Editar producto" : "Nuevo producto"}
        subtitulo="Datos generales y formula de produccion."
        size="wide"
        onClose={cerrarModal}
        footer={
          <>
            <button className="btn" type="button" onClick={cerrarModal}>Cancelar</button>
            <button className="btn btn-primary" form="producto-form" type="submit" disabled={cargando}>Guardar</button>
          </>
        }
      >
        {error && <div className="alert alert-error">{error}</div>}
        <form id="producto-form" onSubmit={guardarProducto}>
          <section className="form-section">
            <h3>Identificacion</h3>
            <div className="form-grid">
              {editando && <Field label="Codigo SKU" value={formulario.codigoSku || ""} disabled />}
              <Field label="Nombre *" name="nombre" value={formulario.nombre} onChange={handleChange} />
              <Select label="Tipo" name="tipo" value={formulario.tipo} onChange={handleChange} options={tiposProducto} />
              <Select label="Tipo de medida" name="tipoMedida" value={formulario.tipoMedida} onChange={handleChange} options={tiposMedida} />
              <Select label="Unidad base" name="unidadMedida" value={formulario.unidadMedida} onChange={handleChange} options={unidadesDe(formulario.tipoMedida)} />
              <Field
                label={`Stock minimo (${formulario.unidadMedida})`}
                type="number"
                min="0"
                step="0.001"
                name="stockMinimo"
                value={formulario.stockMinimo}
                onChange={handleChange}
              />
              <label className="field-check">
                <input type="checkbox" name="activo" checked={formulario.activo} onChange={handleChange} />
                Activo
              </label>
              <div className="field searchable-select multiselect-field product-provider-picker">
                <label>Proveedores</label>
                <div className="product-provider-picker-input">
                  <Search size={16} />
                  <input
                    type="search"
                    value={busquedaProveedor}
                    onFocus={() => setProveedoresAbiertos(true)}
                    onBlur={() => window.setTimeout(() => setProveedoresAbiertos(false), 140)}
                    onChange={(event) => {
                      setBusquedaProveedor(event.target.value);
                      setProveedoresAbiertos(true);
                    }}
                    placeholder="Buscar y seleccionar proveedores..."
                    autoComplete="off"
                  />
                </div>
                {proveedoresAbiertos && (
                  <div className="searchable-options product-provider-picker-options">
                    {proveedoresFiltrados.length === 0 ? (
                      <div className="searchable-empty">Sin resultados</div>
                    ) : proveedoresFiltrados.map((proveedor) => {
                      const seleccionado = (formulario.proveedoresIds || [])
                        .some((id) => String(id) === String(proveedor._id));
                      return (
                        <button
                          className={seleccionado ? "is-selected" : ""}
                          key={proveedor._id}
                          type="button"
                          disabled={proveedor.activo === false && !seleccionado}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => alternarProveedor(proveedor._id)}
                        >
                          <span className="product-provider-check" aria-hidden="true">{seleccionado ? "✓" : ""}</span>
                          <span><strong>{proveedor.razonSocial}</strong><small>{proveedor.codigoProveedor || proveedor.numeroDocumento || "Sin codigo"}</small></span>
                        </button>
                      );
                    })}
                  </div>
                )}
                <div className="product-provider-selected">
                  {(formulario.proveedoresIds || []).length === 0 ? (
                    <span className="section-help">Sin proveedores seleccionados.</span>
                  ) : proveedores.filter((proveedor) => (formulario.proveedoresIds || []).some((id) => String(id) === String(proveedor._id))).map((proveedor) => (
                    <button key={proveedor._id} type="button" onClick={() => alternarProveedor(proveedor._id)} title={`Quitar ${proveedor.razonSocial}`}>
                      {proveedor.razonSocial}<span aria-hidden="true">×</span>
                    </button>
                  ))}
                </div>
                <p className="section-help">La asociacion se crea sin precio. El costo de compra se completa desde el proveedor.</p>
              </div>
            </div>
          </section>

          {permiteFormula(formulario.tipo) && (
            <section className="form-section">
              <div className="section-title-actions">
                <h3>Formula</h3>
                <button className="btn" type="button" onClick={agregarIngrediente}>
                  <Plus size={17} />
                  Agregar ingrediente
                </button>
              </div>

              <div className="form-grid">
                <Field
                  label="Tipo medida producida"
                  value={formulario.tipoMedida}
                  disabled
                />
                <Field
                  label="Cantidad producida *"
                  type="number"
                  step="0.001"
                  name="cantidad"
                  value={formulario.recetaRendimiento.cantidad}
                  onChange={handleRendimientoChange}
                />
                <Field
                  label="Unidad producida"
                  value={formulario.unidadMedida}
                  disabled
                />
                <Field
                  label="Margen de ganancia (%)"
                  type="number"
                  min="0"
                  step="0.01"
                  name="margenGananciaPorcentaje"
                  value={formulario.margenGananciaPorcentaje}
                  onChange={handleChange}
                />
              </div>

              <div className="ops-line-list formula-ingredients-list">
                {(formulario.receta || []).length === 0 ? (
                  <div className="empty-inline">Sin ingredientes definidos.</div>
                ) : (
                  formulario.receta.map((item, index) => (
                    <div className="ops-line product-formula-line" key={item._id || index}>
                      <SearchableSelect
                        label="Producto"
                        value={item.productoId}
                        onChange={(value) => actualizarIngrediente(index, "productoId", value)}
                        options={ingredientesDisponibles.map((producto) => ({
                          value: producto._id,
                          label: `${producto.nombre} - ${producto.tipo}`,
                        }))}
                        placeholder="Buscar producto..."
                      />
                      <Field label="Tipo" value={item.tipo || "-"} disabled />
                      <Field label="Tipo medida" value={item.tipoMedida || "-"} disabled />
                      <Field
                        label="Cantidad *"
                        type="number"
                        step="0.001"
                        value={item.cantidad}
                        onChange={(event) => actualizarIngrediente(index, "cantidad", event.target.value)}
                      />
                      <Select
                        label="Unidad"
                        value={item.unidadMedida}
                        onChange={(event) => actualizarIngrediente(index, "unidadMedida", event.target.value)}
                        options={unidadesDe(item.tipoMedida)}
                      />
                      <button className="btn btn-danger product-remove" type="button" onClick={() => quitarIngrediente(index)}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))
                )}
              </div>

              <div className="section-title-actions formula-materials-title">
                <h3>Materiales de produccion</h3>
                <button className="btn" type="button" onClick={agregarMaterial}>
                  <Plus size={17} />
                  Agregar material
                </button>
              </div>
              <div className="ops-line-list">
                {(formulario.materialesProduccion || []).length === 0 ? (
                  <div className="empty-inline">Sin materiales definidos.</div>
                ) : (
                  formulario.materialesProduccion.map((item, index) => {
                    const materialCatalogo = materialesProduccion.find(
                      (material) => String(material._id) === String(item.materialId),
                    );
                    return (
                      <div className="ops-line product-material-line" key={item._id || index}>
                        <SearchableSelect
                          label="Material"
                          value={item.materialId}
                          onChange={(value) => actualizarMaterial(index, "materialId", value)}
                          options={materialesProduccion.map((material) => ({
                            value: material._id,
                            label: material.nombre,
                          }))}
                          placeholder="Buscar material..."
                        />
                        <Field
                          label="Cantidad por produccion"
                          type="number"
                          min="0"
                          step="1"
                          value={item.cantidad}
                          onChange={(event) => actualizarMaterial(index, "cantidad", event.target.value)}
                        />
                        <Field
                          label="Stock actual"
                          value={`${Number(materialCatalogo?.stockActual || 0).toLocaleString("es-AR")} unidades`}
                          disabled
                        />
                        <button className="btn btn-danger product-remove" type="button" onClick={() => quitarMaterial(index)}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </section>
          )}

        </form>
      </Modal>
      <ProductoDetalleModal producto={detalle} proveedores={proveedores} onClose={() => setDetalle(null)} />
      <ProductoCostosModal
        producto={productoSimulacion}
        productos={productos}
        proveedores={proveedores}
        cargando={cargando}
        onGuardar={guardarPrecioSimulado}
        onClose={() => setProductoSimulacion(null)}
      />
      <PresentacionesGeneralesModal
        abierto={modalPresentacionesGenerales}
        presentaciones={presentacionesGenerales}
        cargando={cargando}
        onGuardar={guardarPresentacionesGenerales}
        onClose={() => setModalPresentacionesGenerales(false)}
      />
    </div>
  );
}

function PresentacionesGeneralesModal({ abierto, presentaciones: iniciales, cargando, onGuardar, onClose }) {
  const [presentaciones, setPresentaciones] = useState([]);

  useEffect(() => {
    if (abierto) setPresentaciones((iniciales || []).map((item) => ({ ...item })));
  }, [abierto, iniciales]);

  if (!abierto) return null;
  const unidades = (tipo) => tipo === "Volumen" ? ["ml", "l"] : ["mg", "g", "kg"];
  const agregar = () => setPresentaciones((actual) => [
    ...actual,
    { tipoMedida: "Peso", cantidad: "", unidadMedida: "g" },
  ]);
  const actualizar = (index, name, value) => setPresentaciones((actual) => actual.map((item, itemIndex) => {
    if (itemIndex !== index) return item;
    if (name === "tipoMedida") return { ...item, tipoMedida: value, unidadMedida: value === "Volumen" ? "ml" : "g" };
    return { ...item, [name]: value };
  }));
  const quitar = (index) => setPresentaciones((actual) => actual.filter((_, itemIndex) => itemIndex !== index));
  const validas = presentaciones.every((item) => Number(item.cantidad) > 0);

  return (
    <Modal abierto titulo="Presentaciones generales" subtitulo="Se aplican a todos los productos finales compatibles." size="wide" onClose={onClose} footer={(
      <>
        <button className="btn" type="button" onClick={onClose}>Cancelar</button>
        <button className="btn btn-primary" type="button" disabled={cargando || !validas} onClick={() => onGuardar(presentaciones.map((item) => ({
          tipoMedida: item.tipoMedida,
          cantidad: Number(item.cantidad),
          unidadMedida: item.unidadMedida,
        })))}>Guardar presentaciones</button>
      </>
    )}>
      <div className="section-title-actions">
        <h3>Plantillas disponibles</h3>
        <button className="btn" type="button" onClick={agregar}><Plus size={17} />Agregar</button>
      </div>
      {presentaciones.length === 0 ? <div className="empty-inline">Sin presentaciones generales.</div> : (
        <div className="table-wrap">
          <table className="product-presentations-table">
            <thead><tr><th>Tipo</th><th>Cantidad</th><th>Unidad</th><th>Vista previa</th><th>Quitar</th></tr></thead>
            <tbody>{presentaciones.map((item, index) => (
              <tr key={item._id || index}>
                <td><select value={item.tipoMedida} onChange={(event) => actualizar(index, "tipoMedida", event.target.value)}><option>Peso</option><option>Volumen</option></select></td>
                <td><input type="number" min="0" step="0.001" value={item.cantidad} onChange={(event) => actualizar(index, "cantidad", event.target.value)} /></td>
                <td><select value={item.unidadMedida} onChange={(event) => actualizar(index, "unidadMedida", event.target.value)}>{unidades(item.tipoMedida).map((unidad) => <option key={unidad}>{unidad}</option>)}</select></td>
                <td><strong>{Number(item.cantidad || 0).toLocaleString("es-AR")} {item.unidadMedida}</strong></td>
                <td><button className="btn btn-danger action-icon-button" type="button" title="Quitar" aria-label="Quitar" onClick={() => quitar(index)}><Trash2 size={16} /></button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

function ProductoCostosModal({ producto, productos, proveedores, cargando, onGuardar, onClose }) {
  const [selecciones, setSelecciones] = useState({});
  const [margen, setMargen] = useState(0);
  const [precioManual, setPrecioManual] = useState(null);
  const [presentacionesProducto, setPresentacionesProducto] = useState([]);

  useEffect(() => {
    setSelecciones({});
    setMargen(Number(producto?.margenGananciaPorcentaje || 0));
    setPrecioManual(Number(producto?.precioVentaPorUnidad || 0) > 0
      ? String(producto.precioVentaPorUnidad)
      : null);
    setPresentacionesProducto((producto?.presentacionesFraccionamiento || []).map((item) => ({
      cantidad: item.cantidad,
      unidadMedida: item.unidadMedida,
      precioVenta: item.precioVenta || "",
      origenGeneral: item.origenGeneral === true,
    })));
  }, [producto]);

  const simulacion = useMemo(() => {
    if (!producto) return null;
    const productosPorId = new Map((productos || []).map((item) => [String(item._id), item]));
    const productosPorNombre = new Map((productos || []).map((item) => [normalizarTexto(item.nombre), item]));
    const componentes = new Map();
    const errores = new Set();
    const baseProducto = producto.tipoMedida === "Volumen" ? "l" : "kg";
    const rendimientoBase = convertirUnidad(
      producto.recetaRendimiento?.cantidad,
      producto.recetaRendimiento?.unidadMedida,
      baseProducto,
    );

    const agregar = ({ tipo, id, nombre, cantidad, unidad }) => {
      const clave = `${tipo}:${id || normalizarTexto(nombre)}`;
      const actual = componentes.get(clave);
      if (actual) actual.cantidad += cantidad;
      else componentes.set(clave, { clave, tipo, id, nombre, cantidad, unidad });
    };

    const expandir = (actual, cantidadRecetas, recorrido = []) => {
      const actualId = String(actual?._id || normalizarTexto(actual?.nombre));
      if (recorrido.includes(actualId)) {
        errores.add(`Referencia circular en la receta de ${actual?.nombre || "producto"}.`);
        return;
      }
      (actual.materialesProduccion || []).forEach((material) => agregar({
        tipo: "material",
        id: idReferencia(material.materialId),
        nombre: material.nombre,
        cantidad: Number(material.cantidad || 0) * cantidadRecetas,
        unidad: "unidad",
      }));
      (actual.receta || []).forEach((ingrediente) => {
        const cantidad = Number(ingrediente.cantidad || 0) * cantidadRecetas;
        if (ingrediente.tipo === "Materia prima") {
          const base = ingrediente.tipoMedida === "Volumen" ? "l" : "kg";
          const cantidadBase = convertirUnidad(cantidad, ingrediente.unidadMedida, base);
          if (cantidadBase === null) errores.add(`No se pudo convertir la unidad de ${ingrediente.producto}.`);
          else agregar({
            tipo: "ingrediente",
            id: idReferencia(ingrediente.productoId),
            nombre: ingrediente.producto,
            cantidad: cantidadBase,
            unidad: base,
          });
          return;
        }
        const intermedio = productosPorId.get(idReferencia(ingrediente.productoId)) ||
          productosPorNombre.get(normalizarTexto(ingrediente.producto));
        const rendimiento = intermedio?.recetaRendimiento;
        const cantidadIntermedia = intermedio
          ? convertirUnidad(cantidad, ingrediente.unidadMedida, rendimiento?.unidadMedida)
          : null;
        if (!intermedio || cantidadIntermedia === null || Number(rendimiento?.cantidad || 0) <= 0) {
          errores.add(`No se pudo calcular el intermedio ${ingrediente.producto}.`);
          return;
        }
        expandir(intermedio, cantidadIntermedia / Number(rendimiento.cantidad), [...recorrido, actualId]);
      });
    };

    if (!rendimientoBase || rendimientoBase <= 0) errores.add("El producto no tiene un rendimiento valido.");
    else expandir(producto, 1 / rendimientoBase);

    const lineas = [...componentes.values()].map((componente) => {
      const opciones = (proveedores || []).map((proveedor) => {
        const asociado = componente.tipo === "material"
          ? (proveedor.materiales || []).find((item) => item.activo !== false &&
              ((componente.id && idReferencia(item.materialProduccionId) === componente.id) ||
                normalizarTexto(item.nombre) === normalizarTexto(componente.nombre)))
          : (proveedor.productos || []).find((item) => item.activo !== false &&
              ((componente.id && idReferencia(item.productoCatalogoId) === componente.id) ||
                normalizarTexto(item.nombre) === normalizarTexto(componente.nombre)));
        if (!asociado) return null;
        return {
          proveedor,
          precio: Number(asociado.precioCompraPorUnidad ?? asociado.precioPorKg ?? 0),
        };
      }).filter(Boolean).sort((a, b) => {
        if (a.precio <= 0 && b.precio > 0) return 1;
        if (b.precio <= 0 && a.precio > 0) return -1;
        return a.precio - b.precio;
      });
      const seleccionada = opciones.find((item) => String(item.proveedor._id) === String(selecciones[componente.clave])) ||
        opciones[0] || null;
      return {
        ...componente,
        opciones,
        seleccionada,
        costoTotal: componente.cantidad * Number(seleccionada?.precio || 0),
      };
    });
    const costoIngredientes = lineas.filter((item) => item.tipo === "ingrediente")
      .reduce((total, item) => total + item.costoTotal, 0);
    const costoMateriales = lineas.filter((item) => item.tipo === "material")
      .reduce((total, item) => total + item.costoTotal, 0);
    const costoTotal = costoIngredientes + costoMateriales;
    return {
      lineas,
      errores: [...errores],
      faltantes: lineas.filter((item) => !item.seleccionada || item.seleccionada.precio <= 0),
      costoIngredientes,
      costoMateriales,
      costoTotal,
    };
  }, [producto, productos, proveedores, selecciones]);

  if (!producto) return null;
  const precioCalculado = Number(simulacion?.costoTotal || 0) * (1 + Number(margen || 0) / 100);
  const precioVenta = precioManual === null ? precioCalculado : Number(precioManual || 0);
  const unidadPrecio = producto.tipoMedida === "Volumen" ? "l" : "kg";
  const unidadesPresentacion = producto.tipoMedida === "Volumen" ? ["ml", "l"] : ["mg", "g", "kg"];
  const precioSugeridoPresentacion = (item) => Math.round(
    Number(convertirUnidad(item.cantidad, item.unidadMedida, unidadPrecio) || 0) * precioVenta * 100,
  ) / 100;
  const agregarPresentacion = () => setPresentacionesProducto((actual) => [...actual, {
    cantidad: "",
    unidadMedida: producto.tipoMedida === "Volumen" ? "ml" : "g",
    precioVenta: "",
    origenGeneral: false,
  }]);
  const actualizarPresentacion = (index, name, value) => setPresentacionesProducto((actual) => actual.map(
    (item, itemIndex) => itemIndex === index ? { ...item, [name]: value } : item,
  ));
  const quitarPresentacion = (index) => setPresentacionesProducto((actual) => actual.filter(
    (item, itemIndex) => itemIndex !== index || item.origenGeneral,
  ));
  const presentacionesValidas = presentacionesProducto.every(
    (item) => Number(item.cantidad) > 0 && Number(item.precioVenta || precioSugeridoPresentacion(item)) > 0,
  );
  const puedeGuardar = simulacion && simulacion.errores.length === 0 && simulacion.faltantes.length === 0 && precioVenta > 0 && presentacionesValidas;

  return (
    <Modal
      abierto
      titulo={`Simulacion de costos: ${producto.nombre}`}
      subtitulo={`Costo y precio de venta para 1 ${producto.tipoMedida === "Volumen" ? "L" : "kg"}.`}
      size="comparison"
      onClose={onClose}
      footer={(
        <>
          <button className="btn" type="button" onClick={onClose}>Cerrar</button>
          <button
            className="btn btn-primary"
            type="button"
            disabled={cargando || !puedeGuardar}
            onClick={() => onGuardar({
              margenGananciaPorcentaje: Number(margen || 0),
              costoCalculadoPorUnidad: Number(simulacion.costoTotal || 0),
              precioVentaPorUnidad: precioVenta,
              presentacionesFraccionamiento: presentacionesProducto.map((item) => ({
                cantidad: Number(item.cantidad),
                unidadMedida: item.unidadMedida,
                precioVenta: Number(item.precioVenta || precioSugeridoPresentacion(item)),
                origenGeneral: item.origenGeneral === true,
              })),
            })}
          >
            Guardar precio de venta
          </button>
        </>
      )}
    >
      {simulacion?.errores.map((mensaje) => <div className="alert alert-error" key={mensaje}>{mensaje}</div>)}
      <div className="table-wrap simulation-table">
        <table>
          <thead><tr><th>Componente</th><th>Tipo</th><th>Cantidad</th><th>Proveedor</th><th>Costo unitario</th><th>Subtotal</th></tr></thead>
          <tbody>
            {(simulacion?.lineas || []).map((linea) => (
              <tr key={linea.clave}>
                <td><strong>{linea.nombre}</strong></td>
                <td>{linea.tipo === "material" ? "Material" : "Materia prima"}</td>
                <td>{formatearCantidad(linea.cantidad)} {linea.unidad}</td>
                <td>
                  <select
                    className="simulation-provider-select"
                    value={linea.seleccionada?.proveedor?._id || ""}
                    onChange={(event) => {
                      setSelecciones((prev) => ({ ...prev, [linea.clave]: event.target.value }));
                      setPrecioManual(null);
                    }}
                  >
                    {linea.opciones.length === 0 && <option value="">Sin proveedor</option>}
                    {linea.opciones.map((opcion) => (
                      <option key={opcion.proveedor._id} value={opcion.proveedor._id}>
                        {opcion.proveedor.razonSocial} - {formatearImporte(opcion.precio)}
                      </option>
                    ))}
                  </select>
                </td>
                <td>{formatearImporte(linea.seleccionada?.precio)} / {linea.unidad}</td>
                <td><strong>{formatearImporte(linea.costoTotal)}</strong></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {simulacion?.faltantes.length > 0 && (
        <div className="alert alert-error">Hay componentes sin un precio de proveedor valido.</div>
      )}
      <div className="simulation-totals">
        <div><span>Ingredientes</span><strong>{formatearImporte(simulacion?.costoIngredientes)}</strong></div>
        <div><span>Materiales</span><strong>{formatearImporte(simulacion?.costoMateriales)}</strong></div>
        <div className="simulation-grand-total"><span>Costo por {producto.tipoMedida === "Volumen" ? "L" : "kg"}</span><strong>{formatearImporte(simulacion?.costoTotal)}</strong></div>
        <div className="field">
          <label>Margen de ganancia (%)</label>
          <input type="number" min="0" step="0.01" value={margen} onChange={(event) => { setMargen(event.target.value); setPrecioManual(null); }} />
        </div>
        <div>
          <span>Precio sugerido segun margen</span>
          <strong>{formatearImporte(precioCalculado)}</strong>
        </div>
        <div className="field simulation-sale-price-edit">
          <label>Precio de venta por {producto.tipoMedida === "Volumen" ? "L" : "kg"}</label>
          <input type="number" min="0" step="0.01" value={precioManual === null ? precioCalculado : precioManual} onChange={(event) => setPrecioManual(event.target.value)} />
        </div>
      </div>
      {producto.tipo === "Final" && (
        <section className="simulation-presentations-section">
          <div className="section-title-actions">
            <div>
              <h3>Presentaciones y precios de venta</h3>
              <p className="section-help">Las presentaciones generales se identifican como heredadas. Sus precios se ajustan para este producto.</p>
            </div>
            <button className="btn" type="button" onClick={agregarPresentacion}><Plus size={17} />Agregar especifica</button>
          </div>
          {presentacionesProducto.length === 0 ? <div className="empty-inline">Sin presentaciones configuradas.</div> : (
            <div className="table-wrap">
              <table className="product-presentations-table">
                <thead><tr><th>Origen</th><th>Cantidad</th><th>Unidad</th><th>Precio sugerido</th><th>Precio de venta</th><th>Acciones</th></tr></thead>
                <tbody>{presentacionesProducto.map((item, index) => (
                  <tr key={`${item.origenGeneral ? "general" : "propia"}-${index}`}>
                    <td><span className={`badge ${item.origenGeneral ? "ok" : "warn"}`}>{item.origenGeneral ? "General" : "Especifica"}</span></td>
                    <td><input type="number" min="0" step="0.001" value={item.cantidad} disabled={item.origenGeneral} onChange={(event) => actualizarPresentacion(index, "cantidad", event.target.value)} /></td>
                    <td><select value={item.unidadMedida} disabled={item.origenGeneral} onChange={(event) => actualizarPresentacion(index, "unidadMedida", event.target.value)}>{unidadesPresentacion.map((unidad) => <option key={unidad}>{unidad}</option>)}</select></td>
                    <td>{formatearImporte(precioSugeridoPresentacion(item))}</td>
                    <td><input type="number" min="0" step="0.01" value={item.precioVenta} onChange={(event) => actualizarPresentacion(index, "precioVenta", event.target.value)} /></td>
                    <td>
                      <div className="row-actions">
                        <button className="btn action-icon-button" type="button" title="Aplicar precio sugerido" aria-label="Aplicar precio sugerido" onClick={() => actualizarPresentacion(index, "precioVenta", precioSugeridoPresentacion(item))}><Calculator size={16} /></button>
                        {!item.origenGeneral && <button className="btn btn-danger action-icon-button" type="button" title="Quitar" aria-label="Quitar" onClick={() => quitarPresentacion(index)}><Trash2 size={16} /></button>}
                      </div>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </Modal>
  );
}

function Field({ label, ...props }) {
  return (
    <div className="field">
      <label>{label}</label>
      <input type="text" {...props} />
    </div>
  );
}

function Select({ label, options, ...props }) {
  return (
    <div className="field">
      <label>{label}</label>
      <select {...props}>
        {options.map((option) => {
          const value = typeof option === "string" ? option : option.value;
          const text = typeof option === "string" ? option : option.label;
          return <option key={value || text} value={value}>{text}</option>;
        })}
      </select>
    </div>
  );
}

function SearchableSelect({ label, value, options, onChange, placeholder = "Buscar..." }) {
  const [texto, setTexto] = useState("");
  const [abierto, setAbierto] = useState(false);
  const seleccion = options.find((option) => String(getOptionValue(option)) === String(value || ""));
  const etiquetaSeleccionada = seleccion ? getOptionLabel(seleccion) : "";
  const query = texto.trim().toLowerCase();
  const filtradas = options
    .filter((option) => getOptionLabel(option).toLowerCase().includes(query))
    .slice(0, 20);

  useEffect(() => {
    if (etiquetaSeleccionada) setTexto(etiquetaSeleccionada);
  }, [etiquetaSeleccionada]);

  const elegir = (option) => {
    setTexto(getOptionLabel(option));
    setAbierto(false);
    onChange(getOptionValue(option));
  };

  return (
    <div className="field searchable-select">
      <label>{label}</label>
      <input
        type="text"
        value={texto}
        placeholder={placeholder}
        onFocus={() => setAbierto(true)}
        onBlur={() =>
          window.setTimeout(() => {
            setAbierto(false);
            if (!value) setTexto("");
          }, 120)
        }
        onChange={(event) => {
          setTexto(event.target.value);
          setAbierto(true);
          if (value) onChange("");
        }}
        autoComplete="off"
      />
      {abierto && (
        <div className="searchable-options">
          {filtradas.length === 0 ? (
            <div className="searchable-empty">Sin resultados</div>
          ) : (
            filtradas.map((option) => (
              <button
                key={getOptionValue(option)}
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => elegir(option)}
              >
                {getOptionLabel(option)}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function ProductoDetalleModal({ producto, proveedores, onClose }) {
  if (!producto) return null;
  const receta = Array.isArray(producto.receta) ? producto.receta : [];
  const materiales = Array.isArray(producto.materialesProduccion) ? producto.materialesProduccion : [];
  const rendimiento = producto.recetaRendimiento || {};
  const proveedoresAsignados = (proveedores || []).filter((proveedor) =>
    (proveedor.productos || []).some(
      (item) => String(item.productoCatalogoId || "") === String(producto._id || ""),
    ),
  );

  return (
    <Modal
      abierto
      titulo={`Detalle ${producto.nombre || ""}`}
      subtitulo="Datos completos del producto."
      size="wide"
      onClose={onClose}
      footer={<button className="btn" type="button" onClick={onClose}>Cerrar</button>}
    >
      <div className="order-detail">
        <section className="order-detail-summary">
          <DetailItem label="SKU" value={producto.codigoSku} />
          <DetailItem label="Nombre" value={producto.nombre} />
          <DetailItem label="Tipo" value={producto.tipo} />
          <DetailItem label="Tipo medida" value={producto.tipoMedida} />
          <DetailItem label="Unidad base" value={producto.unidadMedida} />
          <DetailItem
            label="Stock minimo"
            value={`${Number(producto.stockMinimo || 0).toLocaleString("es-AR")} ${producto.unidadMedida || ""}`}
          />
          <DetailItem label="Activo" value={producto.activo !== false ? "Si" : "No"} />
          {permiteFormula(producto.tipo) && (
            <>
              <DetailItem
                label={`Costo calculado por ${producto.tipoMedida === "Volumen" ? "L" : "kg"}`}
                value={formatearImporte(producto.costoCalculadoPorUnidad)}
              />
              <DetailItem
                label={`Precio de venta por ${producto.tipoMedida === "Volumen" ? "L" : "kg"}`}
                value={formatearImporte(producto.precioVentaPorUnidad)}
              />
            </>
          )}
        </section>

        <section className="order-detail-section">
          <h3>Proveedores asociados</h3>
          {proveedoresAsignados.length === 0 ? (
            <div className="empty-inline">Sin proveedores asociados.</div>
          ) : (
            <div className="tag-list">
              {proveedoresAsignados.map((proveedor) => (
                <span className="tag" key={proveedor._id}>
                  {proveedor.razonSocial}{proveedor.activo === false ? " (Inactivo)" : ""}
                </span>
              ))}
            </div>
          )}
        </section>

        {permiteFormula(producto.tipo) && (
          <section className="order-detail-section">
            <h3>Formula</h3>
            <div className="recipe-result">
              <strong>
                Produce {Number(rendimiento.cantidad || 0).toLocaleString("es-AR")} {rendimiento.unidadMedida || ""}
              </strong>
              <p>Tipo de medida: {rendimiento.tipoMedida || "-"}</p>
              <p>
                Margen de ganancia: {Number(producto.margenGananciaPorcentaje || 0).toLocaleString("es-AR")}%
              </p>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>Tipo</th>
                    <th>Tipo medida</th>
                    <th>Cantidad</th>
                  </tr>
                </thead>
                <tbody>
                  {receta.length === 0 ? (
                    <tr><td colSpan="4">Sin formula definida.</td></tr>
                  ) : (
                    receta.map((item, index) => (
                      <tr key={item._id || index}>
                        <td><strong>{item.producto || "-"}</strong></td>
                        <td>{item.tipo || "-"}</td>
                        <td>{item.tipoMedida || "-"}</td>
                        <td>{Number(item.cantidad || 0).toLocaleString("es-AR")} {item.unidadMedida}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <h3 className="formula-materials-title">Materiales de produccion</h3>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Material</th>
                    <th>Cantidad por produccion</th>
                  </tr>
                </thead>
                <tbody>
                  {materiales.length === 0 ? (
                    <tr><td colSpan="2">Sin materiales definidos.</td></tr>
                  ) : (
                    materiales.map((item, index) => (
                      <tr key={item._id || index}>
                        <td><strong>{item.nombre || "-"}</strong></td>
                        <td>{Number(item.cantidad || 0).toLocaleString("es-AR")} unidades</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>
    </Modal>
  );
}

function DetailItem({ label, value }) {
  const visibleValue = value === null || value === undefined || value === "" ? "-" : value;
  return (
    <div className="detail-item">
      <span>{label}</span>
      <strong>{visibleValue}</strong>
    </div>
  );
}
